"use client";

// Client-only helpers for emitting anonymous marketing telemetry.
// Best-effort and never throws: storage failures degrade to an in-memory id.

import type { MarketingEventName } from "@/lib/marketing/events";

const VISITOR_ID_KEY = "ultrackMarketingVid";
const SESSION_ID_KEY = "ultrackMarketingSid";

// In-memory fallbacks used when Web Storage is unavailable (e.g. private mode,
// blocked cookies/storage, SSR-less environments).
let inMemoryVisitorId: string | null = null;
let inMemorySessionId: string | null = null;

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the Math.random-based fallback.
  }
  return `vid-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function getPersistedId(
  storage: "localStorage" | "sessionStorage",
  key: string,
  getFallback: () => string,
  setFallback: (value: string) => void
): string {
  if (typeof window === "undefined") {
    const existing = getFallback();
    if (existing) return existing;
    const next = randomId();
    setFallback(next);
    return next;
  }
  try {
    const store = window[storage];
    const existing = store.getItem(key);
    if (existing) return existing;
    const next = randomId();
    store.setItem(key, next);
    return next;
  } catch {
    const existing = getFallback();
    if (existing) return existing;
    const next = randomId();
    setFallback(next);
    return next;
  }
}

/** Stable first-party anon id, persisted in localStorage. Never throws. */
export function getMarketingVisitorId(): string {
  return getPersistedId(
    "localStorage",
    VISITOR_ID_KEY,
    () => inMemoryVisitorId ?? "",
    (value) => {
      inMemoryVisitorId = value;
    }
  );
}

/** Per-tab session id, persisted in sessionStorage. Never throws. */
export function getMarketingSessionId(): string {
  return getPersistedId(
    "sessionStorage",
    SESSION_ID_KEY,
    () => inMemorySessionId ?? "",
    (value) => {
      inMemorySessionId = value;
    }
  );
}

/** Parse UTM params from the current URL. Only present keys are included. */
export function readUtmParams(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
} {
  const result: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  } = {};
  if (typeof window === "undefined") return result;
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content"] as const;
    for (const key of keys) {
      const value = params.get(key);
      if (value) result[key] = value;
    }
  } catch {
    // Best-effort only.
  }
  return result;
}

/**
 * Parse ad-network click identifiers (fbclid / gclid / ttclid) from the current
 * URL. Mirrors readUtmParams: only present keys are included, best-effort
 * try/catch, returns `{}` on the server. These are validated client props that
 * the server appends geo/device enrichment after.
 */
export function readAdClickIds(): {
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
} {
  const result: { fbclid?: string; gclid?: string; ttclid?: string } = {};
  if (typeof window === "undefined") return result;
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ["fbclid", "gclid", "ttclid"] as const;
    for (const key of keys) {
      const value = params.get(key);
      if (value) result[key] = value;
    }
  } catch {
    // Best-effort only.
  }
  return result;
}

/**
 * Map a "pretty path" landing URL to a marketing source, e.g. `/get/tiktok`
 * → "tiktok". Lets us share query-string-free links on socials (TikTok bio,
 * Instagram bio) while still attributing the visit. Returns undefined for any
 * path that isn't `/get/<source>`. Lowercased so it matches the acquisition
 * dashboard's canonical source labels. Pure + exported for tests.
 */
export function sourceFromPathname(pathname: string): string | undefined {
  const match = pathname.match(/^\/get\/([a-z0-9_-]+)\/?$/i);
  return match ? match[1].toLowerCase() : undefined;
}

/**
 * Resolve marketing UTM attribution for the current page.
 *
 * Precedence: an explicit `?utm_source=` query param always wins; otherwise a
 * clean `/get/<source>` landing path attributes the visit. Other UTM fields
 * pass through from the query string untouched. Single source of truth used by
 * both the page-view event and the Play Store install-referrer link, so a
 * pretty-path visitor is attributed end to end (view → install).
 */
export function resolveMarketingUtm(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
} {
  const utm = readUtmParams();
  if (!utm.utm_source && typeof window !== "undefined") {
    const pathSource = sourceFromPathname(window.location.pathname);
    if (pathSource) utm.utm_source = pathSource;
  }
  return utm;
}

function getReferrerHost(): string | undefined {
  try {
    if (typeof document === "undefined" || !document.referrer) return undefined;
    return new URL(document.referrer).host || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Fire-and-forget a marketing event to the public ingest endpoint.
 * Strips undefined prop values, swallows all errors, and is a no-op on the
 * server.
 */
export async function postMarketingEvent(
  eventName: MarketingEventName,
  extraProps?: Record<string, string | undefined>
): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const rawProps: Record<string, string | undefined> = {
      ...resolveMarketingUtm(),
      ...readAdClickIds(),
      referrer_host: getReferrerHost(),
      path: window.location.pathname,
      ...extraProps,
    };
    const props: Record<string, string> = {};
    for (const [key, value] of Object.entries(rawProps)) {
      if (value !== undefined) props[key] = value;
    }
    const body = {
      eventName,
      anonId: getMarketingVisitorId(),
      sessionId: getMarketingSessionId(),
      props,
    };
    await fetch("/api/marketing/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
      cache: "no-store",
    });
  } catch {
    // Best-effort only — never surface marketing telemetry failures.
  }
}
